import { createMatchList, getMatchPlayerScore, getPlayerId } from './bracket';
import {
  BRACKET_FORMATS,
  BRACKET_MODES,
  getParticipantsInHeightRange,
  refreshBracketSet,
  validateHeightRanges,
} from './bracketSet';

// 프론트 bracketSet ↔ koreavsa-be /admin/competition-brackets 저장 형식 변환

const MODE_TO_API = {
  [BRACKET_MODES.COMBINED]: 'COMBINED',
  [BRACKET_MODES.GENDER]: 'GENDER',
  [BRACKET_MODES.HEIGHT]: 'HEIGHT',
};

const MODE_FROM_API = Object.fromEntries(
  Object.entries(MODE_TO_API).map(([mode, apiMode]) => [apiMode, mode])
);

const FORMAT_TO_API = {
  [BRACKET_FORMATS.TOURNAMENT]: 'TOURNAMENT',
  [BRACKET_FORMATS.MATCHES]: 'MATCHES',
};

function serializeMatches(bracket) {
  return bracket.rounds.flatMap((round, roundIndex) =>
    round.matches.map((match, matchIndex) => {
      const score1 = getMatchPlayerScore(match, match.player1);
      const score2 = getMatchPlayerScore(match, match.player2);
      return {
        roundIndex,
        matchIndex,
        player1Id: getPlayerId(match.player1),
        player2Id: getPlayerId(match.player2),
        winnerId: getPlayerId(match.winner),
        draw: Boolean(match.draw),
        player1HeadScore: score1?.head ?? null,
        player1BodyScore: score1?.body ?? null,
        player1TotalScore: score1?.total ?? null,
        player2HeadScore: score2?.head ?? null,
        player2BodyScore: score2?.body ?? null,
        player2TotalScore: score2?.total ?? null,
        rounds: (match.roundScores ?? [])
          .map((r) => ({
            round: r.round,
            player1: toApiScore(r.scores?.[String(getPlayerId(match.player1))]),
            player2: toApiScore(r.scores?.[String(getPlayerId(match.player2))]),
          }))
          .filter((r) => r.player1 || r.player2),
      };
    })
  );
}

function toApiScore(score) {
  if (!score) return null;
  return { headScore: score.head ?? 0, bodyScore: score.body ?? 0, totalScore: score.total ?? null };
}

function toScore(head, body, total) {
  if (head == null && body == null && total == null) return null;
  return { head: head ?? null, body: body ?? null, total: total ?? null };
}

/** 저장 요청 본문 (version 제외) */
export function toBracketPayload(bracketSet) {
  const divisions = [];

  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    if (bracketSet.combined) {
      divisions.push({
        key: 'combined',
        label: null,
        bracketSize: bracketSet.combined.bracketSize,
        matches: serializeMatches(bracketSet.combined),
      });
    }
  } else {
    Object.entries(bracketSet.divisions ?? {}).forEach(([key, division]) => {
      if (!division?.bracket) return;
      divisions.push({
        key,
        label: division.label ?? null,
        gender: bracketSet.mode === BRACKET_MODES.GENDER ? key : null,
        minHeight: division.min ?? null,
        maxHeight: division.max ?? null,
        bracketSize: division.bracket.bracketSize,
        matches: serializeMatches(division.bracket),
      });
    });
  }

  return {
    format: FORMAT_TO_API[bracketSet.format] ?? 'TOURNAMENT',
    mode: MODE_TO_API[bracketSet.mode] ?? 'COMBINED',
    heightRanges: bracketSet.heightRanges ?? [],
    selectedDivisionKey: bracketSet.selectedHeightRangeId ?? null,
    divisions,
  };
}

/** generateBracket 과 같은 라운드 구성 (8강 → 4강 → 결승) */
const emptyMatch = () => ({
  player1: null,
  player2: null,
  winner: null,
  draw: false,
  scores: {},
  roundScores: [],
  tbd: false,
});

function createEmptyRounds(bracketSize) {
  const rounds = [
    {
      title: `${bracketSize}강`,
      roundIndex: 0,
      matches: Array.from({ length: bracketSize / 2 }, emptyMatch),
    },
  ];

  let matchCount = bracketSize / 4;
  while (matchCount >= 1) {
    const title = matchCount === 1 ? '결승' : `${matchCount * 2}강`;
    rounds.push({
      title,
      roundIndex: rounds.length,
      matches: Array.from({ length: matchCount }, emptyMatch),
    });
    if (matchCount === 1) break;
    matchCount /= 2;
  }
  return rounds;
}

/** 1:1 경기는 bracketSize = 경기 수, 라운드 1개 */
function buildBracket(division, resolve, matchFormat) {
  const base = matchFormat
    ? createMatchList(Array.from({ length: division.bracketSize ?? 0 }, emptyMatch))
    : { bracketSize: division.bracketSize, rounds: createEmptyRounds(division.bracketSize), champion: null };
  const { rounds } = base;
  (division.matches ?? []).forEach((m) => {
    const match = rounds[m.roundIndex]?.matches[m.matchIndex];
    if (!match) return;
    match.player1 = resolve(m.player1Id);
    match.player2 = resolve(m.player2Id);
    match.winner = resolve(m.winnerId);
    match.draw = Boolean(m.draw);
    const score1 = toScore(m.player1HeadScore, m.player1BodyScore, m.player1TotalScore);
    const score2 = toScore(m.player2HeadScore, m.player2BodyScore, m.player2TotalScore);
    if (score1 && m.player1Id != null) match.scores[String(m.player1Id)] = score1;
    if (score2 && m.player2Id != null) match.scores[String(m.player2Id)] = score2;
    match.roundScores = (m.rounds ?? [])
      .map((r) => {
        const scores = {};
        const p1 = r.player1 && toScore(r.player1.headScore, r.player1.bodyScore, r.player1.totalScore);
        const p2 = r.player2 && toScore(r.player2.headScore, r.player2.bodyScore, r.player2.totalScore);
        if (p1 && m.player1Id != null) scores[String(m.player1Id)] = p1;
        if (p2 && m.player2Id != null) scores[String(m.player2Id)] = p2;
        return { round: r.round, scores };
      })
      .filter((r) => Object.keys(r.scores).length > 0);
  });
  return base;
}

/** 서버 응답 → bracketSet (참가자 정보는 현재 참가자 목록으로 채움) */
export function fromBracketResponse(data, participants) {
  const byId = new Map(participants.map((p) => [String(getPlayerId(p)), p]));
  const resolve = (id) => (id == null ? null : byId.get(String(id)) ?? null);
  const mode = MODE_FROM_API[data.mode] ?? BRACKET_MODES.COMBINED;
  const format = data.format === 'MATCHES' ? BRACKET_FORMATS.MATCHES : BRACKET_FORMATS.TOURNAMENT;
  const matchFormat = format === BRACKET_FORMATS.MATCHES;
  const serverDivisions = data.divisions ?? [];

  let bracketSet;
  if (mode === BRACKET_MODES.COMBINED) {
    const combined = serverDivisions[0];
    bracketSet = {
      format,
      mode,
      combined: combined ? buildBracket(combined, resolve, matchFormat) : null,
      divisions: {},
      heightRanges: [],
      selectedHeightRangeId: null,
    };
  } else {
    const divisions = {};
    serverDivisions.forEach((division) => {
      divisions[division.key] = {
        label: division.label,
        ...(mode === BRACKET_MODES.HEIGHT
          ? { min: Number(division.minHeight), max: Number(division.maxHeight) }
          : {}),
        bracket: buildBracket(division, resolve, matchFormat),
      };
    });
    bracketSet = {
      format,
      mode,
      combined: null,
      divisions,
      heightRanges: mode === BRACKET_MODES.HEIGHT ? data.heightRanges ?? [] : [],
      selectedHeightRangeId: mode === BRACKET_MODES.HEIGHT ? data.selectedDivisionKey : null,
    };
  }

  // 승자 진출 재계산 + 참가자 변동 반영 (기존 저장 로직과 동일)
  return refreshBracketSet(bracketSet, participants);
}

/**
 * 저장된 대진표와 현재 참가자 목록의 차이
 * removed: 대진표에 있지만 취소된 참가자 수, added: 대진표에 없는 새 참가자 수
 */
export function getBracketParticipantDiff(data, participants) {
  const stored = new Set();
  (data?.divisions ?? []).forEach((division) => {
    (division.matches ?? [])
      .filter((m) => m.roundIndex === 0)
      .forEach((m) => {
        if (m.player1Id != null) stored.add(String(m.player1Id));
        if (m.player2Id != null) stored.add(String(m.player2Id));
      });
  });

  let candidates = participants;
  if (data?.mode === 'HEIGHT') {
    const { ranges } = validateHeightRanges(data.heightRanges ?? []);
    const inRange = new Set(
      (ranges ?? []).flatMap((range) =>
        getParticipantsInHeightRange(participants, range).map((p) => String(getPlayerId(p)))
      )
    );
    candidates = participants.filter((p) => inRange.has(String(getPlayerId(p))));
  }

  const current = new Set(candidates.map((p) => String(getPlayerId(p))));
  const removed = [...stored].filter((id) => !current.has(id)).length;
  const added = [...current].filter((id) => !stored.has(id)).length;
  return { removed, added };
}
